import React from 'react';
import styled from 'styled-components';

// ============================================================
//     TYPES
// ============================================================
interface CollapsibleProps extends React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;
  /**
   * Controlled open state
   */
  open: boolean;
}

// ============================================================
//     STYLES
// ============================================================
const StyledCollapsible = styled.div({
  display: 'grid',
  gridTemplateRows: '0fr',

  '&[data-open="true"]': {
    gridTemplateRows: '1fr',
  },

  transitionProperty: 'grid-template-rows',
  transitionDuration: 'var(--cl-duration-300)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',
});

const StyledCollapsibleInner = styled.div({
  overflow: 'hidden',
  minHeight: 0,

  // Removes the (visually hidden) content from the tab order while closed.
  // The transition keeps it visible until the height animation completes.
  visibility: 'hidden',
  '&[data-open="true"]': {
    visibility: 'visible',
  },
  transitionProperty: 'visibility',
  transitionDuration: 'var(--cl-duration-300)',
});

// ============================================================
//     COMPONENT
// ============================================================
/**
 * A minimal controlled collapsible region.
 *
 * Daffy-local stand-in for the Frontend-Platform `Collapsible` atom (the
 * component-library `Collapse` is uncontrolled after first render and can't back
 * the sidebar's persisted open state). Animates open/closed via the
 * `grid-template-rows: 0fr/1fr` technique, so content height stays automatic.
 *
 * @example
 * ```tsx
 * <Collapsible open={isOpen}>
 *   <Sidebar.Menu isSubmenu>...</Sidebar.Menu>
 * </Collapsible>
 * ```
 */
export const Collapsible = (props: CollapsibleProps) => {
  const { children, open, ...restProps } = props;

  return (
    <StyledCollapsible {...restProps} data-component="Collapsible" data-open={open}>
      <StyledCollapsibleInner aria-hidden={!open} data-open={open}>
        {children}
      </StyledCollapsibleInner>
    </StyledCollapsible>
  );
};

Collapsible.displayName = 'Collapsible' as const;
